// src/components/common/PlaceholderSelector.js
// Placeholder Selector Modal Component
import React, { useState } from 'react';
import { X, Search, Copy, Check } from 'lucide-react';
import { PLACEHOLDERS, PLACEHOLDER_CATEGORIES } from '../../utils/documentPlaceholders';

const PlaceholderSelector = ({ isOpen, onClose, onInsert }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [activeCategory, setActiveCategory] = useState('EMPLOYEE');
    const [copiedId, setCopiedId] = useState(null);

    if (!isOpen) return null;

    const handleInsert = (placeholder) => {
        onInsert(placeholder.placeholder);
        // Show copied feedback
        setCopiedId(placeholder.id);
        setTimeout(() => setCopiedId(null), 1000);
    };

    const filteredPlaceholders = PLACEHOLDERS[activeCategory]?.filter(p =>
        p.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.placeholder.toLowerCase().includes(searchQuery.toLowerCase())
    ) || [];

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                            Insert Placeholder
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            Click on a placeholder to insert it into your document
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Search */}
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="relative">
                        <Search className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search placeholders..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                                focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>
                </div>

                <div className="flex h-[500px]">
                    {/* Category Sidebar */}
                    <div className="w-64 border-r border-gray-200 dark:border-gray-700 overflow-y-auto bg-gray-50 dark:bg-gray-900">
                        {Object.entries(PLACEHOLDER_CATEGORIES).map(([key, label]) => (
                            <button
                                key={key}
                                onClick={() => setActiveCategory(key)}
                                className={`w-full text-left px-4 py-3 transition-colors border-l-4 ${
                                    activeCategory === key
                                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 text-blue-600 dark:text-blue-400 font-medium'
                                        : 'border-transparent hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    {/* Placeholders List */}
                    <div className="flex-1 overflow-y-auto p-4">
                        {filteredPlaceholders.length === 0 ? (
                            <div className="text-center py-12">
                                <p className="text-gray-500 dark:text-gray-400">
                                    No placeholders found
                                </p>
                            </div>
                        ) : (
                            <div className="grid gap-3">
                                {filteredPlaceholders.map((placeholder) => (
                                    <button
                                        key={placeholder.id}
                                        onClick={() => handleInsert(placeholder)}
                                        className="text-left p-4 border border-gray-200 dark:border-gray-700 rounded-lg
                                            hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20
                                            transition-all group"
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <h4 className="font-medium text-gray-900 dark:text-white mb-1">
                                                    {placeholder.label}
                                                </h4>
                                                <code className="text-sm px-2 py-1 bg-gray-100 dark:bg-gray-800 text-blue-600 dark:text-blue-400 rounded">
                                                    {placeholder.placeholder}
                                                </code>
                                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                                                    Example: {placeholder.example}
                                                </p>
                                            </div>
                                            <div className="ml-4">
                                                {copiedId === placeholder.id ? (
                                                    <Check className="w-5 h-5 text-green-500" />
                                                ) : (
                                                    <Copy className="w-5 h-5 text-gray-400 group-hover:text-blue-500" />
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        💡 <strong>Tip:</strong> Placeholders will be automatically replaced with actual data when you generate or send the document.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PlaceholderSelector;
